import React from 'react';
import styled, { css } from 'styled-components';
import TextareaAutosize from 'react-textarea-autosize';
import { PSmall } from './Text';

const InputBox = styled.input`
  height: auto;
  width: 100%;
  background: transparent !important;

  line-height: 28px;
  outline: 0;
  border: 0 !important;
  padding: 0 !important;
  margin: 0;

  caret-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.inputTextActive};

  &::placeholder {
    color: ${({ theme }) => theme.colors.inputTextPlaceholder} !important;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    border-radius: 4px;
    opacity: 0.6;
    color-scheme: dark;
    transition: all 0.3s ease-in-out;

    &:hover {
      opacity: 1;
    }
  }
`;

const InputWithButton = styled.div<{
  isFocused: boolean;
  isDisabled: boolean;
}>`
  height: 52px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  transition: all 0.3s ease-in-out;

  border-radius: 8px;
  width: 100%;
  padding: 12px 20px;
  border-width: 1px;
  border-style: solid;

  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-color: ${({ theme }) => theme.colors.bgActive};

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderActive};
  }
  ${({ isFocused, theme }) =>
    isFocused && `border-color: ${theme.colors.inputBorderActive};`}
`;

const ActionIcon = styled.i<{
  isMultiLine: boolean;
  isValid: boolean;
  isDisabled: boolean;
}>`
  opacity: 0.6;
  transition: all 0.3s ease-in-out;

  ${({ isMultiLine }) =>
    isMultiLine && `align-self: flex-end;   padding-bottom: 4px;`}

  ${({ isValid, theme }) =>
    isValid &&
    `
    opacity: 1;
    cursor: pointer;
    color: ${theme.colors.primary};
  `}
`;

const LabelIcon = styled.i`
  opacity: 0.6;
`;

type InputProps = {
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
  errorMessage?: string;
  onSubmit?: () => void;
  canSubmit?: boolean;
  type?:
    | 'text'
    | 'password'
    | 'link'
    | 'email'
    | 'date'
    | 'datetime-local'
    | 'time';
  className?: string;
  shouldSubmitOnEnter?: boolean;
  shouldFocusAfterDisable?: boolean;
  maxRows?: number;
  minRows?: number;
  readOnly?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  style?: React.CSSProperties;
  min?: string;
  max?: string;
  step?: string;
  autoFocusDelay?: number;
  onBlur?: () => void;
};

export const Input = ({
  placeholder,
  value,
  onChange = () => {},
  isDisabled = false,
  errorMessage = '',
  onSubmit = undefined,
  canSubmit = true,
  type = 'text',
  className = '',
  style,
  shouldSubmitOnEnter = false,
  onBlur = () => {},
  ...rest
}: InputProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const isValid = canSubmit && !!value.length;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      shouldSubmitOnEnter &&
      e.code === 'Enter' &&
      e?.nativeEvent?.shiftKey === false
    ) {
      e.preventDefault();

      if (isValid && onSubmit) {
        onSubmit();
      }
    }
  };

  return (
    <Container className={className} isDisabled={isDisabled} style={style}>
      <InputWithButton isFocused={isFocused} isDisabled={isDisabled}>
        {type === 'link' && <LabelIcon className="fas fa-link" />}
        <InputBox
          {...(type === 'password' ||
          type === 'date' ||
          type === 'datetime-local' ||
          type === 'time'
            ? { type }
            : {})}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            onBlur();
          }}
          onKeyDown={onKeyDown}
          {...(['date', 'datetime-local', 'time'].includes(type) && {
            onClick: (e) => e.currentTarget.showPicker(),
          })}
          {...rest}
        />
        {onSubmit && (
          <ActionIcon
            className="fas fa-paper-plane"
            onClick={isValid ? onSubmit : () => {}}
            isValid={isValid}
            isMultiLine={false}
            isDisabled={isDisabled}
          />
        )}
      </InputWithButton>
      {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
    </Container>
  );
};

const MultiLineInputBox = styled(TextareaAutosize)`
  resize: none;
  height: auto;
  background: transparent;
  line-height: 28px;
  margin: 0;
  outline: 0;
  border: 0;
  padding: 0;
  box-shadow: none;
  width: 100%;

  caret-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.inputTextActive};

  &::placeholder {
    color: ${({ theme }) => theme.colors.inputTextPlaceholder};
  }
`;

const Container = styled.div<{ isDisabled: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  padding-bottom: 12px;

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const ErrorLabel = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
  margin: 12px 20px;
  font-weight: 700;
`;
type MultiLineInputProps = Omit<InputProps, 'onChange'> & {
  maxRows?: number;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  tabIndex?: number;
  submitIconClassName?: string;
};
export const MultiLineInput = ({
  placeholder = '',
  value,
  onChange = () => {},
  onPaste = () => {},
  onSubmit = undefined,
  isDisabled = false,
  canSubmit = true,
  errorMessage = '',
  autoFocus = false,
  readOnly = false,
  className = '',
  shouldSubmitOnEnter = false,
  shouldFocusAfterDisable = false,
  maxRows = 20,
  style,
  tabIndex = 0,
  submitIconClassName = 'fas fa-paper-plane',
  ...rest
}: MultiLineInputProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const isValid = canSubmit && !!value.length;
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      shouldSubmitOnEnter &&
      e.code === 'Enter' &&
      e?.nativeEvent?.shiftKey === false
    ) {
      e.preventDefault();

      if (isValid && onSubmit) {
        onSubmit();
      }
    }
  };

  const moveCursor = React.useCallback(() => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(value.length, value.length);
    }
  }, [value]);

  React.useEffect(() => {
    if (shouldFocusAfterDisable && !isDisabled) {
      focusInput();
      moveCursor();
    }
  }, [isDisabled, shouldFocusAfterDisable, value, moveCursor]);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Container
      className={className}
      isDisabled={isDisabled}
      onClick={focusInput}
      style={style}
    >
      <InputWithButton
        isFocused={isFocused}
        isDisabled={isDisabled}
        style={{
          height: 'auto',
        }}
      >
        <MultiLineInputBox
          ref={inputRef}
          readOnly={isDisabled || readOnly}
          autoFocus={autoFocus}
          maxRows={maxRows}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          tabIndex={tabIndex}
          {...rest}
        />
        {onSubmit && (
          <ActionIcon
            isMultiLine
            className={submitIconClassName}
            onClick={isValid ? onSubmit : () => {}}
            isValid={isValid}
            isDisabled={isDisabled}
          />
        )}
      </InputWithButton>
      {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
    </Container>
  );
};
